import { Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
  Service,
  ServiceStandardUsageUnit,
  UsageUnit,
} from 'common/src/types/enums/Service.enum';
import { IBudget } from 'common/src/types/interfaces';

import DataTable from 'portal/src/components/common/DataTable';
import { FormCard } from 'portal/src/components/common/forms/FormCard';
import FormViewFieldLayout from 'portal/src/components/common/forms/FormViewFieldLayout';
import { numberFormatter } from 'portal/src/utils/numberFormatter';
import useAuth from 'portal/src/auth/useAuth';

const CommodityCostAdjustmentForm = ({
  budget,
  onAdjust,
  selectedYears,
}: {
  budget: Partial<IBudget>;
  onAdjust: (adjustments: Record<string, number>) => void;
  selectedYears?: number[];
}) => {
  const { hasReadOnlyAccess } = useAuth();
  const isReadOnly = hasReadOnlyAccess();

  // Calculate the average costs for each Service Type in the Budget
  const avgCommodityCosts = useMemo(() => {
    const accServiceCosts = budget.monthlyDetails?.reduce(
      (acc, { serviceType, cost, usage }) => {
        const service = serviceType as Service;
        if (!acc[service]) {
          acc[service] = {
            totalCost: 0,
            totalUsage: 0,
            unit: ServiceStandardUsageUnit[service],
          };
        }
        acc[service].totalCost += cost;
        acc[service].totalUsage += usage;
        return acc;
      },
      {} as Record<
        Service,
        { totalCost: number; totalUsage: number; unit: UsageUnit }
      >,
    );

    return Object.entries(accServiceCosts || {}).map(([service, data]) => ({
      averageCost: data.totalCost / data.totalUsage,
      id: service,
      service,
      unit: data.unit,
      totalCost: data.totalCost,
      totalUsage: data.totalUsage,
    }));
  }, [budget.monthlyDetails]);

  const commodityRates = useMemo(() => {
    const commodities = new Map<string, number>();
    budget.monthlyDetails?.forEach(({ serviceType }) => {
      if (!commodities.has(serviceType)) {
        commodities.set(serviceType, 0);
      }
    });
    return Object.fromEntries(commodities);
  }, [budget.monthlyDetails]);

  const { control } = useForm(commodityRates);

  const handleRateChange = (serviceType: string, newRate: number) => {
    onAdjust({ ...commodityRates, [serviceType]: newRate });
  };

  const gridColumns: GridColDef[] = [
    {
      field: 'service',
      headerName: 'Service Type',
      flex: 1,
    },
    {
      field: 'unit',
      headerName: 'Unit',
      flex: 1,
      valueFormatter: (value) => `per ${value}`,
    },
    {
      field: 'totalUsage',
      headerName: 'Total Usage',
      flex: 1,
      renderCell: ({ value }) =>
        numberFormatter(value, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      field: 'totalCost',
      headerName: 'Total Cost',
      flex: 1,
      renderCell: ({ value }) => numberFormatter(value),
    },
    {
      field: 'averageCost',
      headerName: 'Current Cost',
      flex: 1,
      renderCell: ({ value }) =>
        numberFormatter(value, {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 4,
        }),
    },
  ];

  return (
    <FormCard
      title={`Adjust Commodity Costs For ${selectedYears?.join(', ')} Fiscal ${
        selectedYears?.length > 1 ? 'Years' : 'Year'
      }`}
      showContentDivider={false}
    >
      <Stack direction="column" spacing={2}>
        <DataTable
          checkboxSelection={false}
          columns={gridColumns}
          hideFooter
          isRowNavigation={false}
          rows={avgCommodityCosts}
        />
        <FormViewFieldLayout
          control={control}
          mainFieldsConfig={Object.entries(commodityRates).map(
            ([serviceType]) => ({
              label: `${serviceType} Adjustment`,
              name: `commodityRates.${serviceType}`,
              placeholder: `Percentage adjustment`,
              type: 'number',
              min: 0,
              max: 100,
              onChange: (value) =>
                handleRateChange(
                  serviceType,
                  parseFloat(value as unknown as string) || 0,
                ),
              suffix: ' %',
              required: false,
            }),
          )}
          columnNumber={3}
          isReadOnly={isReadOnly}
        />
      </Stack>
    </FormCard>
  );
};

export default CommodityCostAdjustmentForm;
